import type {
  MipsDTO,
  VisitNoteBillingCodeDTO,
  VisitNoteMipsDTO,
} from 'dtos';

type ServiceCode = Pick<VisitNoteBillingCodeDTO, 'code'>;
type MipsWithReportingCodes = MipsDTO & {
  requiredReportingCodes?: string[] | null;
  minimumReportingCodeCount?: number | null;
  maximumReportingCodeCount?: number | null;
};

const getMipsId = (selection: Pick<VisitNoteMipsDTO, 'mipsId' | 'mips'>) => (
  selection.mipsId ?? selection.mips?.mipsId
);

export const getRequiredReportingCodes = (
  selection: Pick<VisitNoteMipsDTO, 'mips'>,
): string[] => (
  (selection.mips as MipsWithReportingCodes | undefined)
    ?.requiredReportingCodes
    ?.filter((code): code is string => Boolean(code)) ?? []
);

const isReportingCodeCountValid = (
  selection: Pick<VisitNoteMipsDTO, 'mipsId' | 'mips'>,
  selectedMips: Pick<VisitNoteMipsDTO, 'mipsId' | 'mips'>[],
  serviceCodes: ServiceCode[],
): boolean => {
  const mips = selection.mips as MipsWithReportingCodes | undefined;
  const reportingCodes = new Set(getRequiredReportingCodes(selection));
  const selectedCodes = new Set(serviceCodes
    .map(({ code }) => code)
    .filter((code): code is string => Boolean(code) && reportingCodes.has(code ?? '')));
  const minimumCodeCount = mips?.minimumReportingCodeCount ?? (reportingCodes.size > 0 ? 1 : 0);
  const maximumCodeCount = mips?.maximumReportingCodeCount ?? (reportingCodes.size > 0 ? 1 : 0);
  if (selectedCodes.size <= maximumCodeCount) {
    return selectedCodes.size >= minimumCodeCount;
  }

  const selectionId = getMipsId(selection);
  const codesUsedByOtherSelections = new Set(selectedMips
    .filter((otherSelection) => getMipsId(otherSelection) !== selectionId)
    .flatMap(getRequiredReportingCodes));
  const codesUsedOnlyByThisSelection = Array.from(selectedCodes)
    .filter((code) => !codesUsedByOtherSelections.has(code));
  const effectiveCodeCount = codesUsedOnlyByThisSelection.length === 0
    ? minimumCodeCount
    : codesUsedOnlyByThisSelection.length;
  return effectiveCodeCount >= minimumCodeCount && effectiveCodeCount <= maximumCodeCount;
};

export function getMipsSelectionsInvalidatedByCodeChange(
  selectedMips: VisitNoteMipsDTO[],
  currentServiceCodes: ServiceCode[],
  nextServiceCodes: ServiceCode[],
): VisitNoteMipsDTO[] {
  return selectedMips.filter((selection) => (
    isReportingCodeCountValid(selection, selectedMips, currentServiceCodes)
      && !isReportingCodeCountValid(selection, selectedMips, nextServiceCodes)
  ));
}

export function removeMipsSelections(
  selectedMips: VisitNoteMipsDTO[],
  selectionsToRemove: VisitNoteMipsDTO[],
): VisitNoteMipsDTO[] {
  const idsToRemove = new Set(selectionsToRemove.map(({ mipsId }) => mipsId));
  return selectedMips.filter(({ mipsId }) => !idsToRemove.has(mipsId));
}

export function getRequiredReportingCodeIndexes(
  selection: Pick<VisitNoteMipsDTO, 'mips'>,
  serviceCodes: ServiceCode[],
): number[] {
  const requiredCodes = new Set(getRequiredReportingCodes(selection));
  return serviceCodes.reduce<number[]>((indexes, serviceCode, index) => {
    if (requiredCodes.has(serviceCode.code ?? '')) {
      indexes.push(index);
    }
    return indexes;
  }, []);
}

export function getRemovableReportingCodeIndexes(
  selection: Pick<VisitNoteMipsDTO, 'mipsId' | 'mips'>,
  selectedMips: Pick<VisitNoteMipsDTO, 'mipsId' | 'mips'>[],
  serviceCodes: ServiceCode[],
): number[] {
  const selectionId = getMipsId(selection);
  const codesRequiredByOtherSelections = new Set(selectedMips
    .filter((otherSelection) => getMipsId(otherSelection) !== selectionId)
    .flatMap(getRequiredReportingCodes));
  const reportingCodes = new Set(getRequiredReportingCodes(selection));

  return serviceCodes.reduce<number[]>((indexes, serviceCode, index) => {
    const code = serviceCode.code ?? '';
    if (reportingCodes.has(code) && !codesRequiredByOtherSelections.has(code)) {
      indexes.push(index);
    }
    return indexes;
  }, []);
}
